import { useMemo } from "react";
import { useFearGreedHistory, getFearGreedColor, getFearGreedLabel } from "@/hooks/useFearGreedHistory";
import { useBTCHistory } from "@/hooks/useBTCHistory";
import { 
  ComposedChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  ReferenceArea,
  Area
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { formatUSD } from "@/lib/format";

interface FearGreedHistoryChartProps {
  days?: number;
  height?: number;
}

// Color stops for F&G segments (continuous gradient effect)
const getFngSegmentColor = (value: number): string => {
  if (value <= 20) return "#ef4444"; // Extreme Fear - Red
  if (value <= 35) return "#f97316"; // Fear - Orange  
  if (value <= 50) return "#eab308"; // Neutral low - Yellow
  if (value <= 65) return "#84cc16"; // Neutral high - Lime
  if (value <= 80) return "#22c55e"; // Greed - Green
  return "#10b981"; // Extreme Greed - Emerald
};

export const FearGreedHistoryChart = ({ days = 90, height = 220 }: FearGreedHistoryChartProps) => {
  const { data: history, isLoading: fngLoading } = useFearGreedHistory(days);
  const { data: btcHistory, isLoading: btcLoading } = useBTCHistory('1Y');

  const isLoading = fngLoading || btcLoading;

  // Merge F&G and BTC data
  const chartData = useMemo(() => {
    if (!history || history.length === 0) return [];

    // Create a map of BTC prices by date
    const btcPriceMap = new Map<string, number>();
    if (btcHistory) {
      btcHistory.forEach((point) => {
        btcPriceMap.set(point.date, point.price);
      });
    }

    // Reverse history (oldest first) and merge with BTC
    return [...history].reverse().map((item) => {
      const btcPrice = btcPriceMap.get(item.date);
      return {
        date: item.date,
        value: item.value,
        classification: item.classification,
        btcPrice: btcPrice || null,
        color: getFngSegmentColor(item.value),
      };
    });
  }, [history, btcHistory]);

  // Create segments for colored line rendering
  const segments = useMemo(() => {
    if (chartData.length < 2) return [];
    
    const segs: Array<{ startIdx: number; endIdx: number; color: string }> = [];
    let currentColor = chartData[0].color;
    let startIdx = 0;

    for (let i = 1; i < chartData.length; i++) {
      const newColor = chartData[i].color;
      if (newColor !== currentColor) {
        segs.push({ startIdx, endIdx: i, color: currentColor });
        currentColor = newColor;
        startIdx = i;
      }
    }
    segs.push({ startIdx, endIdx: chartData.length - 1, color: currentColor });
    
    return segs;
  }, [chartData]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-center gap-3">
          <Skeleton className="h-14 w-14 rounded-full" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-[200px] w-full" />
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No historical data available
      </p>
    );
  }

  // Get latest value
  const latest = history[0];
  const color = getFearGreedColor(latest.value);
  const label = getFearGreedLabel(latest.value);

  // Calculate BTC price range for Y axis
  const btcPrices = chartData.map(d => d.btcPrice).filter((p): p is number => p !== null);
  const btcMin = Math.min(...btcPrices) * 0.95;
  const btcMax = Math.max(...btcPrices) * 1.05;

  return (
    <div className="space-y-3">
      {/* Header with current value and legend */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
            style={{ backgroundColor: color }}
          >
            {latest.value}
          </div>
          <div className="text-left">
            <Badge
              variant="outline"
              className="text-sm font-medium"
              style={{ borderColor: color, color }}
            >
              {label}
            </Badge>
            <p className="text-xs text-muted-foreground mt-0.5">
              {format(new Date(latest.date), "MMM d, yyyy")}
            </p>
          </div>
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(90deg, #ef4444, #22c55e)' }} />
            <span>Fear & Greed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-muted-foreground/60" />
            <span>BTC Price</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 40, left: 0, bottom: 0 }}>
            <defs>
              {/* Gradient for background zones */}
              <linearGradient id="fngZoneGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.15} />
                <stop offset="25%" stopColor="#22c55e" stopOpacity={0.1} />
                <stop offset="50%" stopColor="#eab308" stopOpacity={0.05} />
                <stop offset="75%" stopColor="#f97316" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0.15} />
              </linearGradient>
              
              {/* Dynamic gradient for F&G line */}
              <linearGradient id="fngLineGradient" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#ef4444" />
                <stop offset="25%" stopColor="#f97316" />
                <stop offset="45%" stopColor="#eab308" />
                <stop offset="55%" stopColor="#84cc16" />
                <stop offset="75%" stopColor="#22c55e" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>

            {/* Background zone areas */}
            <ReferenceArea y1={80} y2={100} fill="#10b981" fillOpacity={0.08} />
            <ReferenceArea y1={55} y2={80} fill="#22c55e" fillOpacity={0.05} />
            <ReferenceArea y1={45} y2={55} fill="#eab308" fillOpacity={0.03} />
            <ReferenceArea y1={25} y2={45} fill="#f97316" fillOpacity={0.05} />
            <ReferenceArea y1={0} y2={25} fill="#ef4444" fillOpacity={0.08} />

            <XAxis
              dataKey="date"
              tickFormatter={(date) => format(parseISO(date), "MMM d")}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            
            {/* Left Y axis - F&G Index */}
            <YAxis
              yAxisId="fng"
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              ticks={[20, 40, 60, 80]}
              width={30}
            />
            
            {/* Right Y axis - BTC Price */}
            <YAxis
              yAxisId="btc"
              orientation="right"
              domain={[btcMin, btcMax]}
              tickFormatter={(val) => `$${(val / 1000).toFixed(0)}K`}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={50}
            />

            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-popover/95 backdrop-blur border border-border rounded-lg p-3 shadow-xl">
                      <p className="text-xs text-muted-foreground mb-2">
                        {format(parseISO(data.date), "MMM d, yyyy")}
                      </p>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-xs text-muted-foreground">F&G Index:</span>
                          <span 
                            className="text-sm font-bold"
                            style={{ color: data.color }}
                          >
                            {data.value} - {data.classification}
                          </span>
                        </div>
                        {data.btcPrice && (
                          <div className="flex items-center justify-between gap-4">
                            <span className="text-xs text-muted-foreground">BTC Price:</span>
                            <span className="text-sm font-semibold text-foreground">
                              {formatUSD(data.btcPrice)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />

            {/* BTC Price Line */}
            <Line
              yAxisId="btc"
              type="monotone"
              dataKey="btcPrice"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1.5}
              strokeOpacity={0.5}
              dot={false}
              connectNulls
            />

            {/* F&G Line with gradient color */}
            <Line
              yAxisId="fng"
              type="monotone"
              dataKey="value"
              stroke="url(#fngLineGradient)"
              strokeWidth={2.5}
              dot={false}
            />

            {/* Current value dot */}
            {chartData.length > 0 && (
              <Line
                yAxisId="fng"
                type="monotone"
                dataKey="value"
                stroke="transparent"
                dot={(props) => {
                  const { cx, cy, index, payload } = props;
                  if (index === chartData.length - 1) {
                    return (
                      <g key={`dot-${index}`}>
                        <circle cx={cx} cy={cy} r={6} fill={payload.color} />
                        <circle cx={cx} cy={cy} r={3} fill="white" />
                      </g>
                    );
                  }
                  return <g key={`dot-${index}`} />;
                }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Zone Labels */}
      <div className="flex justify-between text-[10px] px-1">
        <span className="text-red-500 font-medium">Extreme Fear</span>
        <span className="text-orange-500">Fear</span>
        <span className="text-yellow-500">Neutral</span>
        <span className="text-green-500">Greed</span>
        <span className="text-emerald-500 font-medium">Extreme Greed</span>
      </div>

      {/* Gradient Scale Bar */}
      <div className="h-1.5 rounded-full relative overflow-hidden bg-gradient-to-r from-red-500 via-orange-500 via-yellow-500 via-green-500 to-emerald-500">
        <div
          className="absolute top-0 w-2 h-1.5 bg-white rounded-full shadow-md border border-gray-400"
          style={{ left: `calc(${latest.value}% - 4px)` }}
        />
      </div>
    </div>
  );
};
